import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ItemMenu, User } from '../../../shared/models';

/**
 * Side panel component that contains the hamburger/close toggle button
 * and the slide-out panel with navigation and user profile section.
 *
 * Navigation uses accordion-style click-based expanding/collapsing for submenus.
 * Only one submenu can be open at a time.
 */
@Component({
  selector: 'rms-side-panel',
  templateUrl: './side-panel.component.html',
  styleUrls: ['./side-panel.component.scss'],
  standalone: false
})
export class SidePanelComponent {
  @Input() menu: Array<ItemMenu> = [];
  @Input() user: User;
  @Input() isOpen = false;
  @Input() showNavItems = true;

  @Output() panelToggle = new EventEmitter<boolean>();
  @Output() logoutClicked = new EventEmitter<void>();
  @Output() menuItemClicked = new EventEmitter<ItemMenu>();
  @Output() menuEventHandler = new EventEmitter<string>();

  /**
   * Toggles the side panel open/closed state
   */
  toggle(): void {
    this.panelToggle.emit(!this.isOpen);
  }

  /**
   * Closes the side panel
   */
  close(): void {
    this.panelToggle.emit(false);
  }

  /**
   * Handles logout action
   */
  onLogout(): void {
    this.logoutClicked.emit();
  }

  /**
   * Toggles a menu item's submenu open/closed (accordion behavior).
   * Closes all other open submenus when opening a new one.
   */
  toggleSubMenu(event: MouseEvent, item: ItemMenu): void {
    event.stopPropagation();
    if (item.subMenu) {
      const wasOpen = item.open;
      // Close all other submenus first
      this.menu.forEach(menuItem => {
        menuItem.open = false;
      });
      // Toggle the clicked item
      item.open = !wasOpen;
    }
  }

  /**
   * Handles clicking on a menu item
   */
  onMenuItemClick(item: ItemMenu): void {
    if (!item.subMenu) {
      this.menuItemClicked.emit(item);
      this.close();
    }
  }

  /**
   * Handles clicking on a submenu item
   */
  onSubMenuItemClick(subItem: ItemMenu): void {
    this.menuItemClicked.emit(subItem);
    this.close();
  }

  /**
   * Handles menu items that trigger events instead of navigation
   */
  onMenuEventHandler(handler: string): void {
    this.menuEventHandler.emit(handler);
    this.close();
  }

  /**
   * Checks if a link is external (starts with http)
   */
  isExternalLink(link: string): boolean {
    return link?.startsWith('http');
  }
}
